import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import {
  ArrowLeft,
  Zap,
  Trash2,
  Edit,
  Check,
  X,
  Plus,
  ChevronUp,
  ChevronDown,
  SmilePlus,
  Upload,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { saveAutomationTemplateVersion } from '../services/automationTemplatesService';
import { suggestNextVersion } from '../services/hrCollectionsService';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';
// Lazy-loaded so the full emoji picker (~large bundle) only downloads when opened.
const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

const AutomationTemplateOverview = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const automationId = templateId;
  const getBackPath = () => '/crm/automation-templates';

  const [automation, setAutomation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', softwares: [] });
  const [newStepTitle, setNewStepTitle] = useState('');
  const [newSoftware, setNewSoftware] = useState('');
  const [availableSoftwares, setAvailableSoftwares] = useState([]);

  // Emoji picker — targets the display emoji ({ kind: 'display' }) or a step ({ kind: 'step', stepId })
  const [emojiTarget, setEmojiTarget] = useState(null);
  const [emojiSaving, setEmojiSaving] = useState(false);

  // New-version modal
  const [versionOpen, setVersionOpen] = useState(false);
  const [versionNumber, setVersionNumber] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [versionError, setVersionError] = useState('');
  const [versionSaving, setVersionSaving] = useState(false);

  const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'BBA Team';
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const chipGood =
    'inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  const inputClass = `w-full rounded-xl border px-3 py-2 text-sm ${
    darkMode
      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  useEffect(() => {
    const fetchSoftwareTypes = async () => {
      try {
        const collectionNames = ['Core', 'Primary', 'Secondary'];
        const softwareTypes = [];
        for (const collectionName of collectionNames) {
          const typesSnapshot = await getDocs(collection(db, 'technologyStack', collectionName, 'types'));
          typesSnapshot.forEach((typeDoc) => {
            const typeData = typeDoc.data();
            if (typeData && typeData.name) {
              softwareTypes.push({
                id: typeDoc.id,
                name: typeData.name,
                tier: collectionName,
                description: typeData.description || '',
              });
            }
          });
        }
        softwareTypes.sort((a, b) => a.name.localeCompare(b.name));
        setAvailableSoftwares(softwareTypes);
      } catch (error) {
        console.error('Error fetching software types:', error);
      }
    };
    fetchSoftwareTypes();
  }, []);

  const fetchAutomation = async () => {
    const automationRef = doc(db, 'automations', automationId);
    const automationDoc = await getDoc(automationRef);
    if (!automationDoc.exists()) throw new Error('Automation not found');

    const automationData = automationDoc.data();
    const stepsRef = collection(db, `automations/${automationId}/workflowSteps`);
    const stepsSnapshot = await getDocs(stepsRef);
    const steps = stepsSnapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.order || 0) - (b.order || 0));

    const automationWithSteps = {
      id: automationDoc.id,
      ...automationData,
      workflowSteps: steps,
      softwares: automationData.softwares || [],
      emojis: automationData.emojis || [],
    };
    setAutomation(automationWithSteps);
    setEditForm({
      name: automationData.name || '',
      description: automationData.description || '',
      softwares: automationData.softwares || [],
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await fetchAutomation();
        setError(null);
      } catch (err) {
        console.error('Error fetching automation:', err);
        setError('Failed to load automation details');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  // ---- Edit / save details ----
  const handleSave = async () => {
    try {
      const automationRef = doc(db, 'automations', automationId);
      await updateDoc(automationRef, {
        name: editForm.name,
        description: editForm.description,
        softwares: editForm.softwares,
        updatedAt: new Date(),
      });
      setAutomation((prev) => ({
        ...prev,
        name: editForm.name,
        description: editForm.description,
        softwares: editForm.softwares,
        updatedAt: new Date(),
      }));
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating automation:', error);
    }
  };

  const handleCancel = () => {
    setEditForm({
      name: automation.name || '',
      description: automation.description || '',
      softwares: automation.softwares || [],
    });
    setIsEditing(false);
  };

  // ---- Workflow steps (title + emoji only) ----
  const handleAddStep = async () => {
    const title = newStepTitle.trim();
    if (!title) return;
    try {
      const stepsRef = collection(db, `automations/${automationId}/workflowSteps`);
      const newStepData = {
        title,
        emoji: '',
        order: automation.workflowSteps.length + 1,
      };
      const docRef = await addDoc(stepsRef, newStepData);
      setAutomation((prev) => ({
        ...prev,
        workflowSteps: [...prev.workflowSteps, { id: docRef.id, ...newStepData }],
      }));
      setNewStepTitle('');
    } catch (error) {
      console.error('Error adding step:', error);
    }
  };

  const handleDeleteStep = async (stepId) => {
    try {
      await deleteDoc(doc(db, `automations/${automationId}/workflowSteps`, stepId));
      setAutomation((prev) => ({
        ...prev,
        workflowSteps: prev.workflowSteps.filter((step) => step.id !== stepId),
      }));
    } catch (error) {
      console.error('Error deleting step:', error);
    }
  };

  const handleUpdateStep = async (stepId, updatedData) => {
    try {
      const stepRef = doc(db, `automations/${automationId}/workflowSteps`, stepId);
      await updateDoc(stepRef, updatedData);
      setAutomation((prev) => ({
        ...prev,
        workflowSteps: prev.workflowSteps.map((step) =>
          step.id === stepId ? { ...step, ...updatedData } : step
        ),
      }));
    } catch (error) {
      console.error('Error updating step:', error);
    }
  };

  const handleMoveStep = async (stepId, direction) => {
    const steps = [...automation.workflowSteps];
    const currentIndex = steps.findIndex((step) => step.id === stepId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= steps.length) return;

    const tempOrder = steps[currentIndex].order;
    steps[currentIndex].order = steps[newIndex].order;
    steps[newIndex].order = tempOrder;

    try {
      await handleUpdateStep(steps[currentIndex].id, { order: steps[currentIndex].order });
      await handleUpdateStep(steps[newIndex].id, { order: steps[newIndex].order });
      setAutomation((prev) => ({
        ...prev,
        workflowSteps: steps.sort((a, b) => (a.order || 0) - (b.order || 0)),
      }));
    } catch (error) {
      console.error('Error moving step:', error);
    }
  };

  // ---- Software integration ----
  const handleToggleSoftware = (software) => {
    setEditForm((prev) => {
      const softwares = prev.softwares.includes(software)
        ? prev.softwares.filter((s) => s !== software)
        : [...prev.softwares, software];
      return { ...prev, softwares };
    });
  };

  // ---- Emoji picking (display emoji or step emoji, saves immediately) ----
  const pickEmoji = async (emoji) => {
    if (!emojiTarget) return;
    setEmojiSaving(true);
    try {
      if (emojiTarget.kind === 'display') {
        // Single display emoji stored as emojis[0] (portal cards read the first entry)
        await updateDoc(doc(db, 'automations', automationId), {
          emojis: emoji ? [emoji] : [],
          updatedAt: new Date(),
        });
        setAutomation((prev) => ({ ...prev, emojis: emoji ? [emoji] : [] }));
      } else if (emojiTarget.kind === 'step') {
        await handleUpdateStep(emojiTarget.stepId, { emoji: emoji || '' });
      }
      setEmojiTarget(null);
    } catch (err) {
      console.error('Emoji save failed:', err);
    } finally {
      setEmojiSaving(false);
    }
  };

  // ---- Versioning ----
  const openVersionModal = () => {
    setVersionNumber(suggestNextVersion(automation.version));
    setVersionNotes('');
    setVersionError('');
    setVersionOpen(true);
  };

  const submitVersion = async () => {
    if (!versionNumber.trim()) {
      setVersionError('Enter a version number.');
      return;
    }
    setVersionSaving(true);
    setVersionError('');
    try {
      await saveAutomationTemplateVersion({
        automationId,
        version: versionNumber,
        notes: versionNotes,
        user: { uid: currentUser?.uid, name: userName },
      });
      setVersionOpen(false);
      setAutomation((prev) => ({ ...prev, version: versionNumber.trim() }));
    } catch (err) {
      console.error('Version save failed:', err);
      setVersionError(err.message || 'Could not save the version.');
    } finally {
      setVersionSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`p-6 flex items-center justify-center`}>
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading automation details...</p>
        </div>
      </div>
    );
  }

  if (error || !automation) {
    return (
      <div className={`p-6 flex items-center justify-center`}>
        <div className={`text-center ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
          <p>{error || 'Failed to load automation details'}</p>
          <button
            onClick={() => navigate(getBackPath())}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  const displayEmoji = automation.emojis?.[0] || '';

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative">
      {/* Header — back arrow, centered title, actions, all on one row */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(getBackPath())}
            title="Back to Templates"
            className={`shrink-0 p-2 rounded-full transition-colors ${
              darkMode
                ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
            }`}
          >
            <ArrowLeft size={22} />
          </button>

          <div className="flex-1 text-center min-w-0">
            <div className="flex items-center justify-center gap-3">
              {/* Display emoji — click to pick with the shared emoji picker */}
              <button
                type="button"
                onClick={() => setEmojiTarget({ kind: 'display' })}
                title="Choose the display emoji"
                className={`shrink-0 w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {displayEmoji ? (
                  <span className="text-2xl leading-none">{displayEmoji}</span>
                ) : (
                  <SmilePlus className={`w-5 h-5 ${subtle}`} />
                )}
              </button>
              {isEditing ? (
                <input
                  type="text"
                  className={`text-2xl font-bold border-b ${darkMode ? 'border-gray-600' : 'border-gray-300'} focus:border-purple-500 focus:outline-none text-center bg-transparent ${darkMode ? 'text-white' : 'text-gray-900'}`}
                  value={editForm.name}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              ) : (
                <h1 className={`text-2xl font-bold tracking-tight truncate text-gray-900 dark:text-white`}>{automation.name}</h1>
              )}
              <button
                type="button"
                onClick={openVersionModal}
                title="Update the version"
                className={`${chipGood} hover:opacity-80 transition-opacity`}
              >
                <CheckCircle className="w-3 h-3" /> v{automation.version || '1.0'}
              </button>
            </div>
          </div>

          <div className="flex space-x-2 shrink-0">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
                >
                  <Check size={16} className="mr-1" />
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className={`px-3 py-1 ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'} rounded-md flex items-center`}
                >
                  <X size={16} className="mr-1" />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={openVersionModal}
                  className="px-3 py-1 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
                  title="Save the current state as a new version"
                >
                  <Upload size={16} className="mr-1" />
                  Update version
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <Edit size={16} className="mr-1" />
                  Edit
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Description</h2>
              {isEditing ? (
                <textarea
                  className={inputClass}
                  rows={4}
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              ) : (
                <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>{automation.description}</p>
              )}
            </div>

            {/* Workflow Steps — title-only rows, each with its own emoji */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Workflow Steps</h2>

              <div className="space-y-2">
                {automation.workflowSteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={`flex items-center gap-3 px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}
                  >
                    <button
                      type="button"
                      onClick={() => setEmojiTarget({ kind: 'step', stepId: step.id })}
                      title="Choose an emoji for this step"
                      className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center transition-colors ${
                        darkMode ? 'bg-gray-600 hover:bg-gray-500' : 'bg-white hover:bg-gray-200 border border-gray-200'
                      }`}
                    >
                      {step.emoji ? (
                        <span className="text-lg leading-none">{step.emoji}</span>
                      ) : (
                        <SmilePlus className={`w-4 h-4 ${subtle}`} />
                      )}
                    </button>
                    <span className={`shrink-0 text-xs font-semibold w-5 text-center ${subtle}`}>{index + 1}.</span>
                    {isEditing ? (
                      <input
                        type="text"
                        className={`flex-1 min-w-0 rounded-md border px-3 py-1.5 text-sm ${
                          darkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        value={step.title}
                        onChange={(e) => handleUpdateStep(step.id, { title: e.target.value })}
                      />
                    ) : (
                      <span className={`flex-1 min-w-0 text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {step.title}
                      </span>
                    )}
                    {isEditing && (
                      <div className="flex items-center shrink-0">
                        <button
                          onClick={() => handleMoveStep(step.id, 'up')}
                          disabled={index === 0}
                          className={`p-1 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`}
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveStep(step.id, 'down')}
                          disabled={index === automation.workflowSteps.length - 1}
                          className={`p-1 ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-40`}
                          title="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteStep(step.id)}
                          className="p-1 text-red-400 hover:text-red-300"
                          title="Delete step"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {automation.workflowSteps.length === 0 && (
                  <p className={`text-sm text-center py-6 ${subtle}`}>No workflow steps yet</p>
                )}

                {isEditing && (
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      type="text"
                      value={newStepTitle}
                      onChange={(e) => setNewStepTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddStep();
                        }
                      }}
                      placeholder="Step title"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={handleAddStep}
                      disabled={!newStepTitle.trim()}
                      className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      <Plus size={16} /> Add step
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Software Integration Section */}
            <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
              <h2 className={`text-lg font-semibold mb-4 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>Software Integration</h2>

              {isEditing && (
                <div className="mb-4 flex items-center gap-2">
                  <select
                    value={newSoftware}
                    onChange={(e) => setNewSoftware(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select software type...</option>
                    {availableSoftwares
                      .filter((softwareType) => !editForm.softwares.includes(softwareType.id))
                      .map((softwareType) => (
                        <option key={softwareType.id} value={softwareType.id}>
                          {softwareType.name} ({softwareType.tier})
                        </option>
                      ))}
                  </select>
                  <button
                    onClick={() => {
                      if (newSoftware) {
                        handleToggleSoftware(newSoftware);
                        setNewSoftware('');
                      }
                    }}
                    disabled={!newSoftware}
                    className="shrink-0 inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              )}

              <div className="space-y-2">
                {(isEditing ? editForm.softwares : automation.softwares).map((softwareTypeId) => {
                  const softwareType = availableSoftwares.find((s) => s.id === softwareTypeId);
                  return (
                    <div
                      key={softwareTypeId}
                      className={`flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60 rounded-xl`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`shrink-0 h-9 w-9 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center`}>
                          <Zap className={`h-4 w-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                        </div>
                        <span className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {softwareType?.name || softwareTypeId}
                        </span>
                      </div>
                      {isEditing && (
                        <button
                          onClick={() => handleToggleSoftware(softwareTypeId)}
                          className={darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-500'}
                          title="Remove"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
                {(isEditing ? editForm.softwares : automation.softwares).length === 0 && (
                  <p className={`text-sm text-center py-6 ${subtle}`}>No software types added yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* New version modal */}
      <Modal isOpen={versionOpen} onClose={() => !versionSaving && setVersionOpen(false)} maxWidth="md">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Save new version</h2>
        <p className={`text-sm mb-4 ${subtle}`}>{automation.name}</p>

        <label className={labelClass}>
          Version number <span className={`font-normal ${subtle}`}>(auto-suggested)</span>
        </label>
        <input
          type="text"
          value={versionNumber}
          onChange={(e) => setVersionNumber(e.target.value)}
          placeholder="e.g. 1.1, 2.0"
          disabled={versionSaving}
          className={`${inputClass} mb-4`}
        />

        <label className={labelClass}>Change notes (optional)</label>
        <textarea
          value={versionNotes}
          onChange={(e) => setVersionNotes(e.target.value)}
          rows={2}
          placeholder="What changed in this version?"
          disabled={versionSaving}
          className={`${inputClass} mb-2`}
        />
        <p className={`text-xs mb-4 ${subtle}`}>
          Snapshots the template's current name, description, emoji, and steps. Prior versions stay
          available in the history.
        </p>

        {versionError && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> {versionError}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => setVersionOpen(false)}
            disabled={versionSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={submitVersion}
            disabled={versionSaving || !versionNumber.trim()}
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {versionSaving ? 'Saving...' : 'Save version'}
          </button>
        </div>
      </Modal>

      {/* Emoji picker (display emoji and step emojis) */}
      <Modal isOpen={!!emojiTarget} onClose={() => !emojiSaving && setEmojiTarget(null)} maxWidth="xl" zIndex={100}>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1 text-center">
          Choose an emoji
        </h2>
        <p className={`text-sm mb-4 text-center ${subtle}`}>
          {emojiTarget?.kind === 'step'
            ? automation.workflowSteps.find((s) => s.id === emojiTarget.stepId)?.title || 'Step'
            : `${automation.name} — display emoji`}
        </p>
        <div className="flex justify-center">
          {emojiTarget && (
            <React.Suspense
              fallback={
                <div className="flex justify-center items-center h-72 w-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
                </div>
              }
            >
              <EmojiPicker
                onEmojiClick={(emojiData) => !emojiSaving && pickEmoji(emojiData.emoji)}
                theme={darkMode ? 'dark' : 'light'}
                width="100%"
                height={380}
                lazyLoadEmojis
                previewConfig={{ showPreview: false }}
              />
            </React.Suspense>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          {(emojiTarget?.kind === 'step'
            ? !!automation.workflowSteps.find((s) => s.id === emojiTarget.stepId)?.emoji
            : !!displayEmoji) ? (
            <button
              onClick={() => pickEmoji('')}
              disabled={emojiSaving}
              className="px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              Remove emoji
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={() => setEmojiTarget(null)}
            disabled={emojiSaving}
            className={`px-4 py-2 rounded-md text-sm ${
              darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'
            }`}
          >
            {emojiSaving ? 'Saving…' : 'Cancel'}
          </button>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default AutomationTemplateOverview;
